import { reportsPosition } from '../GscTools/metrics';
import { REPORTING_TIMEZONE, formatPeriod, searchTypeLabel } from '../GscTools/period';
import { CONTRIBUTORS } from './scope';
import { caseInsensitive, filterLabel, policyLabel, topicFilters, topicMatch } from './topics';

/**
 * The scope every export carries in its head, so a file that outlives this page still says what
 * it is: which property, which surface, which windows, and every topic as it was defined — its
 * terms, its policy, its case rule and the filters it was sent as.
 *
 * A topic's numbers without its definition beside them are not a measurement of anything: two
 * people with different terms under the same topic name would produce different files under the
 * same heading. The two coverage lines say what no topic file contains — the queries Search
 * Console withheld — and that topics may overlap.
 */
function scopeRows({ t, scope, fetchedAt, extra = [] }) {
    const rows = [
        [t('tools.topic_clusters.export.report'), t('tools.topic_clusters.header')],
        [t('tools.gsc.exports.property'), scope.siteUrl],
        [t('tools.gsc.coverage.current'), formatPeriod(scope.current)],
        [t('tools.gsc.coverage.baseline'), scope.baseline ? formatPeriod(scope.baseline) : ''],
        [t('tools.gsc.controls.search_type'), searchTypeLabel(scope.searchType, t)],
        [t('tools.gsc.controls.country'), scope.country || 'all'],
        [t('tools.gsc.controls.device'), scope.device || 'all'],
        [t('tools.gsc.exports.data_state'), t(`tools.gsc.data_state.${scope.dataState}`)],
        [t('tools.gsc.exports.timezone'), REPORTING_TIMEZONE],
        [t('tools.topic_clusters.export.hidden'), t('tools.topic_clusters.export.hidden_value')],
        [t('tools.topic_clusters.export.overlap'), t('tools.topic_clusters.export.overlap_value')],
        ...extra,
        [t('tools.gsc.exports.fetched_at'), fetchedAt ?? ''],
    ];

    for (const topic of scope.topics) {
        rows.push([
            t('tools.topic_clusters.export.topic'),
            topic.name,
            policyLabel(t, topic.policy),
            topicMatch(topic),
            caseInsensitive(topic)
                ? t('tools.topic_clusters.definition.case_insensitive')
                : t('tools.topic_clusters.definition.case_sensitive'),
            topicFilters(topic).map(filterLabel).join(' AND '),
        ]);
    }

    rows.push([]);
    return rows;
}

/**
 * Block 02 as a file: one row per topic, both windows side by side, aggregated by property. Values
 * are the raw API ones and a metric Search Console did not return stays empty rather than 0.
 */
export function buildComparisonCsv({ t, scope, rows, fetchedAt }) {
    const hasPosition = reportsPosition(scope.searchType);
    const out = scopeRows({
        t,
        scope,
        fetchedAt,
        extra: [[t('tools.topic_clusters.export.aggregation'), 'byProperty']],
    });

    out.push([
        t('tools.topic_clusters.comparison.topic'),
        t('tools.topic_clusters.export.filters'),
        t('tools.gsc.columns.current_clicks'),
        t('tools.gsc.columns.prior_clicks'),
        t('tools.gsc.columns.delta_clicks'),
        t('tools.gsc.columns.delta_percent'),
        t('tools.gsc.columns.current_impressions'),
        t('tools.gsc.columns.prior_impressions'),
        t('tools.gsc.columns.current_ctr'),
        t('tools.gsc.columns.prior_ctr'),
        ...(hasPosition
            ? [t('tools.gsc.columns.current_position'), t('tools.gsc.columns.prior_position')]
            : []),
    ]);

    for (const row of rows) {
        out.push([
            row.key,
            row.filters.join(' AND '),
            row.current?.clicks,
            row.prior?.clicks,
            row.deltaClicks,
            row.deltaPercent,
            row.current?.impressions,
            row.prior?.impressions,
            row.current?.ctr,
            row.prior?.ctr,
            ...(hasPosition ? [row.current?.position, row.prior?.position] : []),
        ]);
    }

    return out;
}

/**
 * Block 04 as a file: every query or page retrieved for the active topic in this generation, not
 * the page of rows on screen, with the view's own aggregation, window and retrieval state.
 */
export function buildContributorsCsv({
    t,
    scope,
    topic,
    view,
    period,
    rows,
    stopReason,
    fetchedAt,
}) {
    const hasPosition = reportsPosition(scope.searchType);
    const isPages = view === CONTRIBUTORS.PAGES;

    const out = scopeRows({
        t,
        scope,
        fetchedAt,
        extra: [
            [t('tools.topic_clusters.export.active_topic'), topic],
            [
                t('tools.topic_clusters.export.contributors'),
                t(`tools.topic_clusters.contributors.views.${view}`),
            ],
            [t('tools.topic_clusters.export.aggregation'), isPages ? 'byPage' : 'byProperty'],
            [t('tools.topic_clusters.export.contributors_window'), formatPeriod(period)],
            [t('tools.topic_clusters.export.retrieval'), t(`tools.gsc.stop.${stopReason}`)],
            [
                t('tools.topic_clusters.export.completeness'),
                t('tools.topic_clusters.contributors.not_inventory'),
            ],
        ],
    });

    out.push([
        isPages ? t('tools.gsc.columns.page') : t('tools.gsc.columns.query'),
        t('tools.gsc.metrics.clicks_short'),
        t('tools.gsc.metrics.impressions_short'),
        t('tools.gsc.metrics.ctr_short'),
        ...(hasPosition ? [t('tools.gsc.metrics.position_short')] : []),
        ...(isPages ? [] : [t('tools.topic_clusters.contributors.also_in')]),
    ]);

    for (const row of rows) {
        out.push([
            row.key,
            row.clicks,
            row.impressions,
            row.ctr,
            ...(hasPosition ? [row.position] : []),
            ...(isPages ? [] : [row.alsoIn.join(' | ')]),
        ]);
    }

    return out;
}
